'use client';

import { chevronIcon } from '@/uiIcons/chevronIcon.css';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { position } from '@/uiPrimitives/layout/position';
import { zIndex } from '@/uiPrimitives/layout/zIndex.css';
import { icon } from '@/uiPrimitives/media/icon';
import { PropsWithChildren } from 'react';
import {
	ScrollInline,
	scrollInlineTranslations,
	scrollLeft,
	scrollRight,
} from './ScrollInline';
import {
	measureAfter,
	measureBefore,
	scrollInlineWrapper,
} from './ScrollInline.css';
import { box } from '@/uiPrimitives/layout/box';
import { pile } from '@/uiPrimitives/layout/pile';
import { indentSize } from '@/uiPrimitives/layout/indentSize.definitions';
import { useShowScrollButtons } from './useShowScrollButtons';

const scrollButtonSide = {
	before: {
		insetInlineSide: 'insetInlineStart',
		adjustIcon: 'flipHorizontal',
		justifyIcon: 'start',
	},
	after: {
		insetInlineSide: 'insetInlineEnd',
		adjustIcon: undefined,
		justifyIcon: 'end',
	},
} as const;

const ScrollButton = ({
	isShowing,
	buttonLabel,
	onClick,
	side,
}: {
	side: 'before' | 'after';
	isShowing: boolean;
	buttonLabel: string;
	onClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}) => {
	return (
		<button
			type="button"
			inert={!isShowing}
			onClick={onClick}
			className={classes(
				zIndex({ zIndex: '1' }),
				position({
					position: 'absolute',
					insetBlock: '0',
					[scrollButtonSide[side].insetInlineSide]: '-1px',
				}),
				box({
					inlineSize: indentSize,
					opacity: isShowing ? '1' : '0',
				}),
				pile({
					justifyItems: 'stretch',
					alignItems: 'stretch',
				}),
			)}
		>
			<span
				className={classes(
					pile({
						justifyItems: scrollButtonSide[side].justifyIcon,
						alignItems: 'center',
					}),
				)}
			>
				<i
					role="img"
					aria-label={buttonLabel}
					className={classes(
						icon({
							icon: chevronIcon,
							adjustIcon: scrollButtonSide[side].adjustIcon,
							fill: 'positiveBlue',
						}),
					)}
				/>
			</span>
		</button>
	);
};

export const ScrollInlineWithSmallButtons = ({
	children,
	className,
}: PropsWithChildren<{
	className?: string;
}>) => {
	const {
		measureBeforeRef,
		measureAfterRef,
		scrollAreaRef,
		showBeforeButton,
		showAfterButton,
	} = useShowScrollButtons();

	return (
		<div
			className={classes(
				className,
				scrollInlineWrapper,
				zIndex({ isolation: 'isolate' }),
			)}
		>
			<ScrollInline
				ref={scrollAreaRef}
				measureBeforeSlot={
					<i className={classes(measureBefore)} ref={measureBeforeRef} />
				}
				measureAfterSlot={
					<i className={classes(measureAfter)} ref={measureAfterRef} />
				}
				scrollbarWidth="none"
				notOverflowing={showBeforeButton === false && showAfterButton === false}
				mask={
					showBeforeButton && showAfterButton
						? 'both'
						: showBeforeButton
							? 'before'
							: showAfterButton
								? 'after'
								: undefined
				}
			>
				{children}
			</ScrollInline>
			<ScrollButton
				side="before"
				isShowing={showBeforeButton ?? false}
				buttonLabel={
					scrollInlineTranslations['Scroll left in previous scroll area']
				}
				onClick={() => {
					const scrollAreaElement = scrollAreaRef.current;
					if (scrollAreaElement == null) return;

					scrollLeft(scrollAreaElement, 2 / 3);
				}}
			/>
			<ScrollButton
				side="after"
				isShowing={showAfterButton ?? false}
				buttonLabel={
					scrollInlineTranslations['Scroll right in previous scroll area']
				}
				onClick={() => {
					const scrollAreaElement = scrollAreaRef.current;
					if (scrollAreaElement == null) return;

					scrollRight(scrollAreaElement, 2 / 3);
				}}
			/>
		</div>
	);
};
